import { useEffect, useState } from "react";
import { api, can } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Edit, Trash2, Scroll } from "lucide-react";
import { toast } from "sonner";

export default function Rules() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", order: 0 });
  const canManage = can(user.role, "founder", "admin");

  const load = () => api.get("/rules").then(r => setRules(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title:"", content:"", order: rules.length }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ title: r.title, content: r.content, order: r.order }); setOpen(true); };

  const submit = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Tüm alanlar gerekli");
    try {
      if (editing) await api.put(`/rules/${editing.id}`, form);
      else await api.post("/rules", form);
      toast.success(editing ? "Kural güncellendi" : "Kural eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.title}" silinsin mi?`)) return;
    try { await api.delete(`/rules/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-5xl">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// RULEBOOK</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Kurallar</h1>
          <p className="text-zinc-500 mt-2">Deulsra'nın uyulması zorunlu ilkeleri.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-rule-button" className="bg-rose-600 hover:bg-rose-700">
                <Plus className="w-4 h-4 mr-2" /> Kural Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Kuralı Düzenle" : "Yeni Kural"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs font-mono">BAŞLIK</Label>
                  <Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="rule-title-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs font-mono">İÇERİK</Label>
                  <Textarea rows={6} value={form.content} onChange={e=>setForm({...form,content:e.target.value})} data-testid="rule-content-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs font-mono">SIRA</Label>
                  <Input type="number" value={form.order} onChange={e=>setForm({...form,order:parseInt(e.target.value)||0})} className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono w-32" /></div>
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="rule-submit-button" className="bg-rose-600 hover:bg-rose-700">Kaydet</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="space-y-3">
        {rules.length === 0 && (
          <Card className="bg-zinc-950 border-zinc-900 p-10 text-center">
            <Scroll className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-500 text-sm">Henüz kural eklenmemiş.</p>
          </Card>
        )}
        {rules.map((r, idx) => (
          <Card key={r.id} className="bg-zinc-950 border-zinc-900 p-6" data-testid={`rule-item-${r.id}`}>
            <div className="flex items-start gap-4">
              <div className="font-mono text-rose-500 text-2xl font-bold w-10 shrink-0">{String(idx+1).padStart(2,'0')}</div>
              <div className="flex-1">
                <h3 className="font-heading text-xl font-semibold mb-2">{r.title}</h3>
                <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">{r.content}</p>
              </div>
              {canManage && (
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="ghost" onClick={()=>openEdit(r)} data-testid={`edit-rule-${r.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>remove(r)} data-testid={`delete-rule-${r.id}`} className="text-rose-400 hover:bg-rose-950/30"><Trash2 className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
